import { useState, useMemo, useEffect, useRef } from "react";
import styles from "./FilterDrawer.module.css";

/**
 * FilterPanel — reusable status-filter component.
 *
 * Props:
 *   variant  "panel" (default) — inline collapsible strip, used in most views
 *            "dropdown"        — floating dropdown anchored to the trigger button,
 *                               used in careManagerDashboard / PriorityTable
 *   className  optional extra class forwarded to the root element
 */
const FilterPanel = ({
  isOpen,
  onToggle,
  statuses,
  activeFilters,
  onFilterChange,
  onApplyFilters,
  filteredData,
  statusesLoading,
  variant = "panel",
  className,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  // Local pending selections for the dropdown variant — only committed on Apply
  const [localFilters, setLocalFilters] = useState([]);
  const dropdownRef = useRef(null);

  const filteredStatuses = useMemo(() => {
    return statuses.filter((status) =>
      status.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [statuses, searchTerm]);

  const getStatusCount = (status) => {
    return filteredData.filter(
      (patient) =>
        patient.statuses &&
        patient.statuses.some((s) => s.status === status.name)
    ).length;
  };

  // Sync local selections with parent every time the dropdown is opened
  useEffect(() => {
    if (variant === "dropdown" && isOpen) {
      setLocalFilters([...activeFilters]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close the dropdown when the user clicks outside it
  useEffect(() => {
    if (variant !== "dropdown" || !isOpen) return;
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onToggle();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [variant, isOpen, onToggle]);

  const handleLocalCheckboxChange = (statusId) => {
    setLocalFilters((prev) =>
      prev.includes(statusId)
        ? prev.filter((id) => id !== statusId)
        : [...prev, statusId]
    );
  };

  const handleApply = () => {
    if (onApplyFilters) onApplyFilters(localFilters);
    onToggle();
  };

  // ── Dropdown variant ────────────────────────────────────────────────────────
  if (variant === "dropdown") {
    return (
      <div ref={dropdownRef} className={`${styles.dropdownWrapper} ${className || ""}`}>
        <div
          className={`${styles.dropdownPanel} ${isOpen ? styles.dropdownPanelOpen : styles.dropdownPanelClosed}`}
          aria-hidden={!isOpen}
        >
          <div className={styles.dropdownList}>
            {statusesLoading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <span>Loading filters...</span>
              </div>
            ) : filteredStatuses.length === 0 ? (
              <div className={styles.emptyState}>No filters available</div>
            ) : (
              filteredStatuses.map((status) => {
                const isActive = localFilters.includes(status.id);
                return (
                  <label
                    key={status.id}
                    className={`${styles.dropdownItem} ${isActive ? styles.dropdownItemActive : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={() => handleLocalCheckboxChange(status.id)}
                      className={styles.dropdownCheckbox}
                    />
                    <span className={styles.dropdownItemLabel}>{status.name}</span>
                  </label>
                );
              })
            )}
          </div>
          <button className={styles.applyButton} onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    );
  }

  // ── Default panel variant ───────────────────────────────────────────────────
  return (
    <div className={`${styles.filterPanel} ${className || ""}`}>
      <div
        className={isOpen ? styles.panelContent : styles.panelContentCollapsed}
      >
        <div className={styles.filtersSection}>
          <div className={styles.filtersList}>
            {statusesLoading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <span>Loading filters...</span>
              </div>
            ) : filteredStatuses.length === 0 ? (
              <div className={styles.emptyState}>
                {searchTerm
                  ? "No filters match your search"
                  : "No filters available"}
              </div>
            ) : (
              <div className={styles.filtersGrid}>
                {filteredStatuses.map((status) => {
                  const count = getStatusCount(status);
                  const isActive = activeFilters.includes(status.id);

                  return (
                    <label
                      key={status.id}
                      className={`${styles.filterItem} ${
                        isActive ? styles.filterItemActive : ""
                      }`}
                    >
                      <div className={styles.filterCheckbox}>
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={() => onFilterChange(status.id)}
                          className={styles.checkbox}
                        />
                        <div className={styles.checkboxCustom}>
                          {isActive && (
                            <div className={styles.checkmark}>✓</div>
                          )}
                        </div>
                      </div>

                      <div className={styles.filterInfo}>
                        <span className={styles.filterName}>{status.name}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
